import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { smartUpload } from "@/lib/upload";
import { UploadProgress } from "@/components/UploadProgress";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function FulfillRequestDialog({ req, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [fulfillForm, setFulfillForm] = useState({ file: null, version: "v1.0", note: "" });
  const [fulfillMode, setFulfillMode] = useState("upload");
  const [linkDocId, setLinkDocId] = useState("");
  const [fulfillProgress, setFulfillProgress] = useState(null);

  useEffect(() => {
    if (req) {
      setFulfillForm({ file: null, version: "v1.0", note: "" });
      setFulfillMode("upload");
      setLinkDocId("");
      setFulfillProgress(null);
    }
  }, [req]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["file-requests", selectedId] });
    qc.invalidateQueries({ queryKey: ["documents", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };

  const fulfillMut = useMutation({
    mutationFn: async () => {
      const direct = await smartUpload(fulfillForm.file, {
        project_id: selectedId,
        party_id: req.target.id,
        file_request_id: req.id,
        category: req.category,
        version: fulfillForm.version,
        note: fulfillForm.note,
      }, setFulfillProgress);
      if (direct) return direct;
      const fd = new FormData();
      fd.append("file", fulfillForm.file);
      fd.append("version", fulfillForm.version);
      fd.append("note", fulfillForm.note);
      return (await api.post(`/file-requests/${req.id}/fulfill`, fd)).data;
    },
    onSuccess: () => {
      toast.success(t("filesPage.toastFulfilled"));
      onClose();
      setFulfillProgress(null);
      invalidate();
    },
    onError: (e) => {
      toast.error(fmtErr(e));
      setFulfillProgress(null);
    },
  });

  const { data: linkableDocs = [] } = useQuery({
    queryKey: ["linkable-docs", selectedId, req?.target?.id],
    queryFn: async () => (await api.get(`/projects/${selectedId}/library/${req.target.id}`)).data.files,
    enabled: !!req && fulfillMode === "link",
  });

  const linkMut = useMutation({
    mutationFn: async () => (await api.post(`/file-requests/${req.id}/fulfill-link`, { document_id: linkDocId })).data,
    onSuccess: () => {
      toast.success(t("filesPage.toastLinked"));
      onClose();
      setLinkDocId("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fulfill-dialog">
        <DialogHeader><DialogTitle>{t("filesPage.fulfillTitle")} — {req?.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t("filesPage.fulfillWarn")}
          </div>
          <div className="flex gap-2" data-testid="fulfill-mode-toggle">
            <Button size="sm" variant={fulfillMode === "upload" ? "default" : "outline"} className={fulfillMode === "upload" ? "bg-slate-900" : ""} onClick={() => setFulfillMode("upload")} data-testid="fulfill-mode-upload">
              {t("filesPage.modeUpload")}
            </Button>
            <Button size="sm" variant={fulfillMode === "link" ? "default" : "outline"} className={fulfillMode === "link" ? "bg-slate-900" : ""} onClick={() => setFulfillMode("link")} data-testid="fulfill-mode-link">
              {t("filesPage.modeLink")}
            </Button>
          </div>

          {fulfillMode === "upload" ? (
            <>
              <div className="space-y-1.5">
                <Label>{t("common.file")}</Label>
                <Input type="file" onChange={(e) => setFulfillForm({ ...fulfillForm, file: e.target.files[0] })} data-testid="fulfill-file-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.version")}</Label>
                <Input value={fulfillForm.version} onChange={(e) => setFulfillForm({ ...fulfillForm, version: e.target.value })} className="font-mono" data-testid="fulfill-version-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.noteOptional")}</Label>
                <Input value={fulfillForm.note} onChange={(e) => setFulfillForm({ ...fulfillForm, note: e.target.value })} data-testid="fulfill-note-input" />
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label>{t("filesPage.linkLabel")}</Label>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="fulfill-link-list">
                {linkableDocs.length === 0 && <p className="py-4 text-center text-xs text-slate-500">{t("filesPage.linkEmpty")}</p>}
                {linkableDocs.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setLinkDocId(d.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-slate-50 ${linkDocId === d.id ? "border-sky-500 bg-sky-50" : ""}`}
                    data-testid={`fulfill-link-doc-${d.id}`}
                  >
                    <span className="truncate font-medium text-slate-800">{d.filename}</span>
                    <span className="shrink-0 font-mono text-slate-500">{d.folder || d.category} · {d.version}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {fulfillMode === "upload" && fulfillProgress && <UploadProgress progress={fulfillProgress} />}
        <DialogFooter>
          {fulfillMode === "upload" ? (
            <Button onClick={() => fulfillMut.mutate()} disabled={!fulfillForm.file || fulfillMut.isPending} data-testid="fulfill-submit-button">
              {fulfillMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("filesPage.uploadFulfill")}
            </Button>
          ) : (
            <Button onClick={() => linkMut.mutate()} disabled={!linkDocId || linkMut.isPending} data-testid="fulfill-link-submit-button">
              {linkMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("filesPage.linkFulfill")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
